/**
 * The text transforms behind the section editor's formatting toolbar.
 *
 * Slack sections carry mrkdwn, which is close enough to markdown to be misleading:
 * bold is `*one asterisk*`, italic is `_underscore_`, and there is no list syntax at
 * all — the bullets in our announcements are literal `•` characters. An operator
 * fixing emphasis at 8am should not have to remember which of those is which, so the
 * toolbar applies them and these functions hold the rules.
 *
 * Each takes the current text plus the textarea's selection and returns the new text
 * with the selection to restore, so the caret ends up where the operator expects and
 * the behaviour is tested rather than eyeballed. The wrap and bullet actions toggle:
 * pressing Bold on already-bold text removes the markers, which is what makes the
 * toolbar usable for *fixing* Claude's emphasis rather than only adding to it.
 */

/** The mrkdwn markers, keyed by the toolbar action that applies them. */
export const MRKDWN_MARKER = {
    bold: '*',
    italic: '_',
    strike: '~',
    code: '`',
} as const;

export type MrkdwnMarker = (typeof MRKDWN_MARKER)[keyof typeof MRKDWN_MARKER];

/**
 * The bullet our announcements use. Slack renders no list markup, so a bulleted
 * line is literally a bullet character and a space.
 */
export const BULLET = '• ';

export interface SelectionEdit {
    text: string;
    selectionStart: number;
    selectionEnd: number;
}

/**
 * Wrap the selection in `marker`, or unwrap it when it is already wrapped — whether
 * the operator selected the markers along with the text or just the text inside them.
 * With nothing selected the markers are inserted and the caret lands between them.
 */
export function wrapSelection(text: string, start: number, end: number, marker: MrkdwnMarker): SelectionEdit {
    const selected = text.slice(start, end);
    const width = marker.length;

    // The selection includes the markers: `*bold*` -> `bold`.
    if (selected.length > width * 2 && selected.startsWith(marker) && selected.endsWith(marker)) {
        const inner = selected.slice(width, selected.length - width);
        return {
            text: text.slice(0, start) + inner + text.slice(end),
            selectionStart: start,
            selectionEnd: start + inner.length,
        };
    }

    // The markers sit just outside the selection: `*[bold]*` -> `[bold]`.
    if (start >= width && text.slice(start - width, start) === marker && text.slice(end, end + width) === marker) {
        return {
            text: text.slice(0, start - width) + selected + text.slice(end + width),
            selectionStart: start - width,
            selectionEnd: end - width,
        };
    }

    return {
        text: text.slice(0, start) + marker + selected + marker + text.slice(end),
        selectionStart: start + width,
        selectionEnd: start + width + selected.length,
    };
}

/**
 * Bullet every line the selection touches, or un-bullet them when they all already
 * are. Blank lines are left alone — a bullet on its own reads as a dropped item.
 */
export function bulletLines(text: string, start: number, end: number): SelectionEdit {
    const lineStart = start === 0 ? 0 : text.lastIndexOf('\n', start - 1) + 1;
    const nextNewline = text.indexOf('\n', end);
    const lineEnd = nextNewline === -1 ? text.length : nextNewline;

    const lines = text.slice(lineStart, lineEnd).split('\n');
    const content = lines.filter((line) => line.trim().length > 0);
    const allBulleted = content.length > 0 && content.every((line) => line.startsWith(BULLET));

    const next = lines
        .map((line) => {
            if (line.trim().length === 0) return line;
            return allBulleted ? line.slice(BULLET.length) : BULLET + line;
        })
        .join('\n');

    return {
        text: text.slice(0, lineStart) + next + text.slice(lineEnd),
        selectionStart: lineStart,
        selectionEnd: lineStart + next.length,
    };
}

/**
 * Turn the selection into a Slack link, `<url|label>`. With nothing selected the bare
 * `<url>` form is inserted, which Slack renders as the URL itself.
 */
export function insertLink(text: string, start: number, end: number, url: string): SelectionEdit {
    const selected = text.slice(start, end);
    const link = selected.length > 0 ? `<${url}|${selected}>` : `<${url}>`;
    return {
        text: text.slice(0, start) + link + text.slice(end),
        selectionStart: start,
        selectionEnd: start + link.length,
    };
}
